import { Loading } from '@byecode/ui'
import { getAssetUrl } from '@lighthouse/assets'
import {
    AppThemeContextProvider,
    getInnerTypeADTValueByStateSchema,
    NoFound,
    PageNoAccess,
    useAtomAction,
    useAtomData
} from '@lighthouse/shared'
import equal from 'fast-deep-equal'
import { useAtomCallback } from 'jotai/utils'
import { find } from 'rambda'
import React, { useCallback, useMemo, useRef } from 'react'
import { useAsyncRetry, useUpdateEffect } from 'react-use'

import { applicationSettingAtom, appRoleAtom } from '@/atoms/application/state'
import { fetchBlocksAtom, fetchPageAtom, openPageStackAtom, setPageStateAtom } from '@/atoms/page/action'
import {
    homePageAtom,
    pagePathParameterFamily,
    pageQueryParameterFamily,
    pageStackAtomFamily,
    pageStatesParameterFamily,
    pageVariableSchemasFamily
} from '@/atoms/page/state'
import { stackFactory } from '@/atoms/page/utils'
import { PageContainer } from '@/containers/PageDetail/PageContainer'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useFetchPageVariable } from '@/hooks/useFetchPageVariable'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'
import { useUserPageList } from '@/hooks/useUserPageList'

interface NestedPageWrapperProps {
    stackId: string
    pageId: string
}

export const NestedPageWrapper: React.FC<NestedPageWrapperProps> = ({ stackId, pageId }) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const userId = useAtomData(appRoleAtom)
    const parseVariable2Field = useParseVariableWithSubscription(undefined, { pageId, stackId })
    const { run: setPageState } = useAtomAction(setPageStateAtom)
    const theme = useAtomData(
        applicationSettingAtom,
        useCallback(s => s?.theme, [])
    )
    const { visitPageList } = useUserPageList()
    const homePage = useAtomData(homePageAtom)
    const { run: openPageStack } = useAtomAction(openPageStackAtom)
    const isVisiblePage = useMemo(() => find(item => item.id === pageId, visitPageList), [pageId, visitPageList])

    const handleGoHome = useCallback(() => {
        if (!homePage) {
            return
        }
        openPageStack(stackFactory({ pageId: homePage.id }))
    }, [homePage, openPageStack])

    const fetchVariables = useFetchPageVariable({ pageId, stackId })
    const res = useAsyncRetry(
        useAtomCallback(
            useCallback(
                async (get, set) => {
                    const pageRes = await set(fetchPageAtom, pageId)
                    if (!pageRes) {
                        return
                    }

                    await Promise.all([fetchVariables(), set(fetchBlocksAtom, { pageId, stackId })])
                    const pageStateParameter = get(pageStatesParameterFamily(pageId))
                    const pageStateWithValue = pageStateParameter?.map(item => ({
                        id: item.id,
                        ...getInnerTypeADTValueByStateSchema(item, {
                            appId,
                            envId,
                            userId,
                            parseVariable2Field
                        })
                    }))
                    setPageState({ stackId, pageState: pageStateWithValue ?? [] })
                    return pageRes
                },
                [pageId, fetchVariables, stackId, setPageState, appId, envId, userId, parseVariable2Field]
            )
        ),
        [pageId, stackId]
    )

    const role = useAtomData(appRoleAtom)
    const lastRoleRef = useRef(role)
    const pageVariableSchemas = useAtomData(pageVariableSchemasFamily(pageId))
    const pagePathParameter = useAtomData(pagePathParameterFamily(pageId))
    const pageQueryParams = useAtomData(pageQueryParameterFamily(pageId))
    const currentStack = useAtomData(pageStackAtomFamily(stackId))
    const runtimeQueryParams = currentStack?.pageQueryParameters
    const runtimePathParams = currentStack?.pagePathParameters
    const isNotSamePage = res.value?.id !== pageId

    // 栈运行时路由参数变化 → 仅重新获取变量（不卸载页面）
    const prevRouteRef = useRef({ stackId, runtimeQueryParams, runtimePathParams })
    useUpdateEffect(() => {
        if (equal(prevRouteRef.current, { stackId, runtimeQueryParams, runtimePathParams })) {
            return
        }
        prevRouteRef.current = { stackId, runtimeQueryParams, runtimePathParams }
        if (isNotSamePage) {
            return
        }
        fetchVariables()
    }, [stackId, runtimeQueryParams, runtimePathParams, isNotSamePage, fetchVariables])

    // 页面数据变量的配置项 schema 变化时，重新获取页面变量
    const latestPageVariableRef = useRef({ pageVariableSchemas, pagePathParameter, pageQueryParams })
    useUpdateEffect(() => {
        if (
            equal(latestPageVariableRef.current, { pageVariableSchemas, pagePathParameter, pageQueryParams }) &&
            lastRoleRef.current === role
        ) {
            return
        }

        latestPageVariableRef.current = { pageVariableSchemas, pagePathParameter, pageQueryParams }
        lastRoleRef.current = role

        if (isNotSamePage) {
            return
        }

        fetchVariables()
    }, [role, isNotSamePage, pageVariableSchemas, pagePathParameter, pageQueryParams])

    if (res.loading || res.value?.id !== pageId) {
        return <Loading />
    }

    if (!res.value) {
        return <NoFound image={getAssetUrl('empty', 'no_page.svg')} title="" desc="找不到页面，页面已被删除" btnText="" />
    }

    if (!isVisiblePage) {
        return <PageNoAccess onGoHome={handleGoHome} />
    }

    return (
        <AppThemeContextProvider theme={theme}>
            <PageContainer pageId={pageId} stackId={stackId} onFreshPage={res.retry} />
        </AppThemeContextProvider>
    )
}
